import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { KoFiConfig, KoFiPosition, KoFiShape, KoFiSize } from '../../shared/types';

const KOFI_SCRIPT_SRC = 'https://storage.ko-fi.com/cdn/scripts/overlay-widget.js';

const DEFAULTS: Required<KoFiConfig> = {
  enabled: true,
  type: 'widget',
  username: 'roblauzon',
  color: '#72a4f2',
  textColor: '#ffffff',
  side: 'right',
  buttonText: 'Support me',
  position: 'bottom-right',
  size: 'medium',
  shape: 'pill',
  offset: 24,
  icon: '☕',
};

const SIZE_STYLES: Record<KoFiSize, { padding: string; fontSize: number; imageHeight: number }> = {
  small:  { padding: '6px 12px',  fontSize: 12, imageHeight: 28 },
  medium: { padding: '9px 18px',  fontSize: 14, imageHeight: 36 },
  large:  { padding: '12px 24px', fontSize: 16, imageHeight: 48 },
};

const SHAPE_RADIUS: Record<KoFiShape, number> = { pill: 999, rounded: 8, square: 0 };

function positionStyle(position: KoFiPosition, offset: number): React.CSSProperties {
  const style: React.CSSProperties = { position: 'fixed', zIndex: 9999 };
  if (position.startsWith('bottom')) style.bottom = offset;
  else style.top = offset;
  if (position.endsWith('right')) style.right = offset;
  else style.left = offset;
  return style;
}

function fetchConfig(): Promise<KoFiConfig | null> {
  return fetch(`/api/site-config?key=kofi&t=${Date.now()}`, { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

/** Strip rendered Ko-Fi DOM (button + iframe) — keep the script & global. */
function removeKofiDom() {
  document.getElementById('kofiframe')?.remove();
  document.querySelectorAll('[id^="kofi-"]').forEach((el) => el.remove());
}

/**
 * Inject the Ko-Fi overlay script once per page session. Cached as a module
 * promise so multiple effect runs share the same load.
 */
let scriptPromise: Promise<void> | null = null;
function loadKofiScript(): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).kofiWidgetOverlay) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${KOFI_SCRIPT_SRC}"]`,
    ) as HTMLScriptElement | null;
    if (existing) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).kofiWidgetOverlay) resolve();
      else existing.addEventListener('load', () => resolve());
      return;
    }
    const script = document.createElement('script');
    script.src = KOFI_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null; // allow retry on next config change
      reject(new Error('Failed to load Ko-Fi overlay script'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Global Ko-Fi support widget. Mounted once outside the route tree in App.tsx.
 *
 * Listens for the `kofi-config-updated` window event (fired by AdminKofi after
 * save) and re-fetches config + re-draws — so admin changes are instant in the
 * editor's tab.
 */
export function KoFiWidget() {
  const [config, setConfig] = useState<KoFiConfig | null>(null);

  // Initial fetch + live-update listener.
  useEffect(() => {
    fetchConfig().then((data) => data && setConfig(data));
    const handler = () => fetchConfig().then((data) => data && setConfig(data));
    window.addEventListener('kofi-config-updated', handler);
    return () => window.removeEventListener('kofi-config-updated', handler);
  }, []);

  // Render the floating-chat widget. Image/button types render in JSX below.
  useEffect(() => {
    if (!config) return;

    // Always clear stale Ko-Fi DOM before deciding what to do.
    removeKofiDom();

    if (!config.enabled || config.type !== 'widget') return;

    let cancelled = false;
    loadKofiScript()
      .then(() => {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const overlay = (window as any).kofiWidgetOverlay;
        overlay?.draw(config.username, {
          'type': 'floating-chat',
          'floating-chat.donateButton.text': config.buttonText,
          'floating-chat.donateButton.background-color': config.color,
          'floating-chat.donateButton.text-color': config.textColor,
        });
      })
      .catch(() => { /* silent — Ko-Fi is non-critical */ });

    return () => { cancelled = true; };
  }, [config]);

  if (!config || !config.enabled || config.type === 'widget' || config.type === 'none') {
    return null;
  }

  const merged = { ...DEFAULTS, ...config };
  const size   = SIZE_STYLES[merged.size];
  const radius = SHAPE_RADIUS[merged.shape];
  const wrapperStyle = positionStyle(merged.position, merged.offset);

  if (merged.type === 'image') {
    return createPortal(
      <div style={wrapperStyle}>
        <a
          href={`https://ko-fi.com/${merged.username}`}
          target="_blank"
          rel="noreferrer"
          aria-label="Support me on Ko-fi"
        >
          <img
            height={size.imageHeight}
            src="https://storage.ko-fi.com/cdn/kofi6.png?v=6"
            style={{ border: 0, height: size.imageHeight, display: 'block' }}
            alt="Buy Me a Coffee at ko-fi.com"
          />
        </a>
      </div>,
      document.body,
    );
  }

  // type === 'button'
  return createPortal(
    <div style={wrapperStyle}>
      <a
        href={`https://ko-fi.com/${merged.username}`}
        target="_blank"
        rel="noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: size.padding,
          backgroundColor: merged.color,
          color: merged.textColor,
          borderRadius: radius,
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: size.fontSize,
          lineHeight: 1,
          boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
          whiteSpace: 'nowrap',
        }}
      >
        {merged.icon && <span style={{ fontSize: size.fontSize + 2 }}>{merged.icon}</span>}
        {merged.buttonText}
      </a>
    </div>,
    document.body,
  );
}
